import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/api';

const AwardsGallery = () => {
    const [awardImages, setAwardImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAwardsImages();
    }, []);

    const fetchAwardsImages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/gallery/get_all.php`);
            const data = await response.json();
            if (data.status === 'success') {
                // Filter for "awards" category and get the latest 6
                const awards = data.data
                    .filter(img => img.category.toLowerCase() === 'awards')
                    .slice(0, 6);
                setAwardImages(awards);
            }
        } catch (error) {
            console.error('Error fetching awards images:', error);
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
        }
    };

    if (loading) {
        return (
            <section className="relative py-24 px-6 bg-[#0a0a0a]">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                </div>
            </section>
        );
    }

    if (awardImages.length === 0) {
        return null; // Don't show section if no awards images
    }

    return (
        <section className="relative py-24 px-6 bg-[#0a0a0a] overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.03, 0.05, 0.03]
                    }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600 blur-[150px] rounded-full"
                ></motion.div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/[0.08] backdrop-blur-md text-blue-400 text-[10px] font-black tracking-[0.2em] uppercase shadow-xl mb-6">
                        <FiAward className="text-sm" />
                        Student Achievements
                    </div>
                    <h2 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-neutral-500 mb-6 tracking-tight">
                        Our <span className="text-gradient">Awards</span> & Recognition
                    </h2>
                    <p className="text-neutral-400 max-w-2xl mx-auto text-lg leading-relaxed">
                        Celebrating the outstanding achievements of our students in mathematics competitions and examinations.
                    </p>
                </motion.div>

                {/* Gallery Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
                >
                    {awardImages.map((image) => (
                        <motion.div
                            key={image.id}
                            variants={itemVariants}
                            className="group relative rounded-2xl overflow-hidden border border-white/5 bg-white/[0.02] backdrop-blur-sm hover:border-blue-500/30 transition-all duration-500"
                        >
                            <div className="aspect-[4/3] overflow-hidden">
                                <img
                                    src={`${API_BASE_URL.replace('/api', '')}/${image.image_url}`}
                                    alt={image.title}
                                    className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                                    loading="lazy"
                                />
                            </div>

                            {/* Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                                <h3 className="text-xl font-bold text-white mb-2">{image.title}</h3>
                                {image.description && (
                                    <p className="text-sm text-neutral-300 line-clamp-2">{image.description}</p>
                                )}
                            </div>

                            {/* Award Badge */}
                            <div className="absolute top-4 right-4 bg-blue-600/90 backdrop-blur-md p-2 rounded-full shadow-lg">
                                <FiAward className="text-white text-lg" />
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* View All Button */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-center"
                >
                    <Link to="/gallery">
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="px-10 py-4 bg-white/[0.03] border border-white/10 backdrop-blur-xl text-white font-black text-sm rounded-full transition-all hover:bg-white/[0.08] hover:border-blue-500/30 flex items-center justify-center gap-3 mx-auto"
                        >
                            View All Gallery <FiArrowRight className="text-sm" />
                        </motion.button>
                    </Link>
                </motion.div>
            </div>

            {/* Global gradient text style */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .text-gradient {
                    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
                `
            }} />
        </section>
    );
};

export default AwardsGallery;
